import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const UnifiedAuthContext = createContext();

export const useAuth = () => useContext(UnifiedAuthContext);

export const UnifiedAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch user data if token exists
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      setLoading(true);
      const response = await axios.get('https://app.akiya2.com/get-user-data', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = { ...response.data, token };
      setUser(userData);
      return userData; // Now actually return the user data
    } catch (err) {
      setUser(null);
      localStorage.removeItem('access_token');
      throw err; // Re-throw so callers can handle the error
    } finally {
      setLoading(false);
    }
  };

  // Login function (for both user and agent)
  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const response = await axios.post('https://app.akiya2.com/login', { email, password });
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        const userData = await fetchUser(response.data.access_token);
        return userData; // Return the actual user data
      } else {
        setError('No access token received');
        return null;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      setUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function (for both user and agent)
  const register = async (registrationData) => {
    setError(null);
    setLoading(true);
    try {
      const response = await axios.post('https://app.akiya2.com/register', registrationData);
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        const userData = await fetchUser(response.data.access_token);
        return userData; // Return user data instead of just true/false
      } else {
        setError('No access token received');
        return null;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      setUser(null);
      throw err; // Let the component handle the error
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
  };

  // Update user profile
  const updateUser = async (newUserData) => {
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.put('https://app.akiya2.com/update-user-data', newUserData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUser = { ...response.data, token };
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      setError(err.response?.data?.detail || 'Update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update user image
  const updateUserImage = async (imageFile) => {
    setError(null);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Access token not found');
      }
  
      // Validate file before upload
      if (!imageFile) {
        throw new Error('No image file provided');
      }
  
      // Check file size (10MB limit to match backend)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (imageFile.size > maxSize) {
        throw new Error('File too large. Maximum size is 10MB');
      }
  
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/tiff'];
      if (!allowedTypes.includes(imageFile.type)) {
        throw new Error('Invalid file type. Please upload a valid image file (JPEG, PNG, WebP, GIF, BMP, or TIFF)');
      }
  
      const formData = new FormData();
      formData.append('file', imageFile);
  
      const response = await axios.put('https://app.akiya2.com/update-user-image', formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (response.data && response.data.message === 'User image updated successfully') {
        console.log('Image upload successful:', {
          size: response.data.file_size_mb + 'MB',
          format: response.data.format,
          url: response.data.image_url
        });
        
        // Refresh user data to get updated image
        await fetchUser(token);
        return {
          success: true,
          data: response.data
        };
      } else {
        throw new Error('Failed to update user image');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      
      // Handle different types of errors
      let errorMessage = 'Image update failed';
      
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        
        switch (status) {
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            break;
          case 413:
            errorMessage = 'File too large. Maximum size is 10MB.';
            break;
          case 400:
            errorMessage = detail || 'Invalid file. Please upload a valid image.';
            break;
          case 404:
            errorMessage = 'User not found.';
            break;
          case 500:
            errorMessage = detail || 'Server error. Please try again later.';
            break;
          default:
            errorMessage = detail || `Upload failed (${status})`;
        }
      } else if (err.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

  // Helpers
  const isAuthenticated = !!user;
  const isAgent = user?.isAgent === true;
  const isAdmin = user?.isAdmin === true;

  return (
    <UnifiedAuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        error,
        login,
        register,
        logout,
        updateUser,
        updateUserImage,
        isAuthenticated,
        isAgent,
        isAdmin,
        showModal,
        setShowModal,
      }}
    >
      {children}
    </UnifiedAuthContext.Provider>
  );
};